/** Codecs Grandstream UCM may offer that Chrome WebRTC rejects (e.g. G726-32 on PT 2). */
const STRIP_LEGACY_AUDIO_PAYLOADS = [
  "G726-32",
  "G726-24",
  "G726-40",
  "G726",
  "G729",
  "G729A",
  "G723",
  "GSM",
  "iLBC",
  "ILBC",
  "speex",
  "G722",
];

const WEBRTC_AUDIO_CODEC = /^(PCMU|PCMA|opus|telephone-event|CN|RED)(\/|$)/i;

const stripPayloadFromSdp = (sdp, payloadName) => {
  const mediaDescs = [];
  const lines = sdp.split(/\r\n/);
  let currentMediaDesc = null;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^m=(?:audio|video)/.test(line)) {
      currentMediaDesc = { index: i, stripped: [] };
      mediaDescs.push(currentMediaDesc);
      continue;
    }
    if (!currentMediaDesc) {
      continue;
    }

    const rtpmap = /^a=rtpmap:(\d+) ([^/]+)\//.exec(line);
    if (rtpmap && payloadName === rtpmap[2]) {
      lines.splice(i, 1);
      currentMediaDesc.stripped.push(rtpmap[1]);
      i -= 1;
    }
  }

  mediaDescs.forEach((mediaDesc) => {
    const mline = lines[mediaDesc.index].split(" ");
    for (let j = 3; j < mline.length; ) {
      if (mediaDesc.stripped.includes(mline[j])) {
        mline.splice(j, 1);
      } else {
        j += 1;
      }
    }
    lines[mediaDesc.index] = mline.join(" ");
  });

  return lines.join("\r\n");
};

const normalizeSdpLines = (sdp) => {
  const normalized = sdp.includes("\r\n") ? sdp : sdp.replace(/\n/g, "\r\n");
  return normalized.split(/\r\n/);
};

const finalizeSdp = (sdp) => {
  const lines = normalizeSdpLines(sdp).filter((line) => line.length > 0);
  return `${lines.join("\r\n")}\r\n`;
};

const hasSessionLevelMid = (sdp) => {
  const lines = normalizeSdpLines(sdp).filter((line) => line.length > 0);
  const firstMedia = findFirstMediaLineIndex(lines);
  if (firstMedia <= 0) {
    return false;
  }
  return lines.slice(0, firstMedia).some((line) => /^a=mid:/.test(line));
};

/**
 * sip.js runs sessionDescriptionHandlerModifiers on both remote and local SDP.
 * UCM sanitization must only touch remote peer SDP; mutating Chrome's local answer
 * breaks setLocalDescription with "Invalid SDP line".
 */
export const shouldSanitizeRemoteDescription = (description) => {
  const sdp = description?.sdp || "";
  if (!sdp) {
    return false;
  }

  return (
    /^m=video /m.test(sdp) ||
    /a=rtpmap:\d+ (?:G726|G729|G729A|G723|GSM|iLBC)/i.test(sdp) ||
    hasSessionLevelMid(sdp) ||
    /a=group:BUNDLE \S+ \S+/m.test(sdp)
  );
};

export const onlyForRemoteDescription = (modifier) => (description) => {
  if (!shouldSanitizeRemoteDescription(description)) {
    const next = { ...description };
    if (next.sdp) {
      next.sdp = finalizeSdp(next.sdp);
    }
    return Promise.resolve(next);
  }
  return modifier(description);
};

export const shouldNormalizeLocalAnswerForUcm = (description) =>
  description?.type === "answer" && Boolean(description?.sdp);

export const shouldNormalizeLocalSdp = (description) =>
  (description?.type === "answer" || description?.type === "offer") &&
  Boolean(description?.sdp);

export const onlyForLocalDescription = (modifier) => (description) => {
  if (!shouldNormalizeLocalAnswerForUcm(description)) {
    const next = { ...description };
    if (next.sdp) {
      next.sdp = finalizeSdp(next.sdp);
    }
    return Promise.resolve(next);
  }
  return modifier(description);
};

export const onlyForLocalSdp = (modifier) => (description) => {
  if (!shouldNormalizeLocalSdp(description)) {
    const next = { ...description };
    if (next.sdp) {
      next.sdp = finalizeSdp(next.sdp);
    }
    return Promise.resolve(next);
  }
  return modifier(description);
};

/**
 * Physical on-prem UCM: prefer G.711 in the local answer, but ONLY keep payload
 * types Chrome already negotiated from the remote offer. Never invent codecs.
 * If G.711 is absent, keep Opus so setLocalDescription stays valid.
 */
export const preferUcmAudioCodecsInLocalAnswer = (description) => {
  const lines = normalizeSdpLines(description.sdp || "").filter((line) => line.length > 0);
  const audioMLineIndex = lines.findIndex((line) => /^m=audio /.test(line));
  if (audioMLineIndex < 0) {
    description.sdp = finalizeSdp(lines.join("\r\n"));
    return Promise.resolve(description);
  }

  const audioSectionEnd = lines.findIndex((line, index) => index > audioMLineIndex && /^m=/.test(line));
  const sectionEnd = audioSectionEnd >= 0 ? audioSectionEnd : lines.length;
  const section = lines.slice(audioMLineIndex, sectionEnd);
  const mLineParts = lines[audioMLineIndex].split(" ");
  const existingPayloads = mLineParts.slice(3);

  const payloadCodecs = new Map();
  section.forEach((line) => {
    const rtpmap = /^a=rtpmap:(\d+) ([^/]+)\//.exec(line);
    if (rtpmap) {
      payloadCodecs.set(rtpmap[1], rtpmap[2].toUpperCase());
    }
  });
  existingPayloads.forEach((payload) => {
    if (!payloadCodecs.has(payload)) {
      if (payload === "0") payloadCodecs.set(payload, "PCMU");
      else if (payload === "8") payloadCodecs.set(payload, "PCMA");
      else if (payload === "101") payloadCodecs.set(payload, "TELEPHONE-EVENT");
    }
  });

  const isG711 = (payload) => /^(PCMU|PCMA)$/.test(payloadCodecs.get(payload) || "");
  const isDtmf = (payload) => /^TELEPHONE-EVENT/.test(payloadCodecs.get(payload) || "");
  const isOpus = (payload) => /^OPUS/.test(payloadCodecs.get(payload) || "");
  const isRed = (payload) => /^RED/.test(payloadCodecs.get(payload) || "");

  const g711 = existingPayloads.filter((payload) => isG711(payload));
  const dtmf = existingPayloads.filter((payload) => isDtmf(payload));
  const opus = existingPayloads.filter((payload) => isOpus(payload));
  const keptPayloads =
    g711.length > 0
      ? [...g711, ...dtmf]
      : [...opus, ...dtmf].filter((payload) => !isRed(payload));

  if (keptPayloads.length === 0) {
    description.sdp = finalizeSdp(lines.join("\r\n"));
    return Promise.resolve(description);
  }

  const keptSet = new Set(keptPayloads);
  const rebuiltMLine = [...mLineParts.slice(0, 3), ...keptPayloads].join(" ");

  const sanitizedSection = section
    .filter((line, index) => {
      if (index === 0) {
        return true;
      }
      const rtpmap = /^a=rtpmap:(\d+) /.exec(line);
      if (rtpmap && !keptSet.has(rtpmap[1])) {
        return false;
      }
      const fmtp = /^a=fmtp:(\d+) /.exec(line);
      if (fmtp && !keptSet.has(fmtp[1])) {
        return false;
      }
      const rtcpFb = /^a=rtcp-fb:(\d+) /.exec(line);
      if (rtcpFb && !keptSet.has(rtcpFb[1])) {
        return false;
      }
      return true;
    })
    .map((line, index) => {
      if (index === 0) {
        return rebuiltMLine;
      }
      if (line === "a=setup:actpass") {
        return "a=setup:active";
      }
      return line;
    });

  const sanitized = [
    ...lines.slice(0, audioMLineIndex),
    ...sanitizedSection,
    ...lines.slice(sectionEnd),
  ].filter((line) => line !== "a=extmap-allow-mixed");

  description.sdp = finalizeSdp(sanitized.join("\r\n"));
  return Promise.resolve(description);
};

/**
 * Physical UCM is on a private LAN. Only drop Chrome mDNS (*.local) host
 * candidates when a real host IP already exists. Never strip .local if that
 * would leave only public srflx/relay addresses the PBX cannot route to.
 */
export const preferRoutableIceCandidates = (description) => {
  const sdp = description?.sdp || "";
  if (!sdp) {
    return Promise.resolve(description);
  }

  const lines = normalizeSdpLines(sdp).filter((line) => line.length > 0);
  const candidateLines = lines.filter((line) => /^a=candidate:/i.test(line));
  if (candidateLines.length === 0) {
    description.sdp = finalizeSdp(lines.join("\r\n"));
    return Promise.resolve(description);
  }

  const hasRealHostIp = candidateLines.some(
    (line) => / typ host\b/i.test(line) && !/\.local\b/i.test(line)
  );
  if (!hasRealHostIp) {
    description.sdp = finalizeSdp(lines.join("\r\n"));
    return Promise.resolve(description);
  }

  const filtered = lines.filter(
    (line) => !(/^a=candidate:/i.test(line) && /\.local\b/i.test(line))
  );
  description.sdp = finalizeSdp(filtered.join("\r\n"));
  return Promise.resolve(description);
};

const findFirstMediaLineIndex = (lines) => lines.findIndex((line) => /^m=/.test(line));

const getMediaSectionMid = (lines, mediaLineIndex) => {
  for (let i = mediaLineIndex + 1; i < lines.length; i += 1) {
    if (/^m=/.test(lines[i])) {
      break;
    }
    const midMatch = /^a=mid:(.+)$/.exec(lines[i]);
    if (midMatch) {
      return midMatch[1];
    }
  }
  return null;
};

const ensureMediaSectionMid = (lines, mediaLineIndex, mid) => {
  if (getMediaSectionMid(lines, mediaLineIndex)) {
    return lines;
  }

  const insertIndex = (() => {
    for (let i = mediaLineIndex + 1; i < lines.length; i += 1) {
      if (/^m=/.test(lines[i])) {
        return mediaLineIndex + 1;
      }
      if (/^c=/.test(lines[i])) {
        return i + 1;
      }
    }
    return mediaLineIndex + 1;
  })();

  const next = [...lines];
  next.splice(insertIndex, 0, `a=mid:${mid}`);
  return next;
};

/**
 * Removes video m-sections from remote SDP for audio-only browser calls.
 * UCM may include video in inbound offers; Chrome rejects them when constraints.video is false.
 * UCM also places a=mid lines at session scope (before m= lines), which Chrome rejects.
 */
export const stripVideoSections = (description) => {
  const sdp = description.sdp || "";
  if (!sdp) {
    return Promise.resolve(description);
  }

  const lines = normalizeSdpLines(sdp);
  const videoMids = new Set();
  const keptLines = [];
  let inVideoSection = false;
  let removedVideo = false;

  for (const line of lines) {
    if (/^m=video /.test(line)) {
      removedVideo = true;
      inVideoSection = true;
      continue;
    }
    if (/^m=/.test(line)) {
      inVideoSection = false;
    }
    if (inVideoSection) {
      const midMatch = /^a=mid:(.+)$/.exec(line);
      if (midMatch) {
        videoMids.add(midMatch[1]);
      }
      continue;
    }
    keptLines.push(line);
  }

  const firstMediaIndex = findFirstMediaLineIndex(keptLines);
  let sanitized = keptLines.filter((line, index) => {
    if (/^a=mid:/.test(line)) {
      const mid = line.slice("a=mid:".length);
      if (videoMids.has(mid)) {
        return false;
      }
      if (firstMediaIndex >= 0 && index < firstMediaIndex) {
        return false;
      }
    }
    return true;
  });

  if (removedVideo) {
    const bundleLine = sanitized.find((line) => /^a=group:BUNDLE /.test(line));
    if (bundleLine) {
      const bundleMids = bundleLine.slice("a=group:BUNDLE ".length).split(" ");
      const mediaCount = sanitized.filter((line) => /^m=/.test(line)).length;
      if (bundleMids.length > mediaCount) {
        bundleMids.slice(mediaCount).forEach((mid) => videoMids.add(mid));
      }
    }
  }

  sanitized = sanitized
    .map((line) => {
      const bundleMatch = /^a=group:BUNDLE (.+)$/.exec(line);
      if (!bundleMatch) {
        return line;
      }
      const mids = bundleMatch[1].split(" ").filter((mid) => !videoMids.has(mid));
      if (mids.length === 0) {
        return null;
      }
      return `a=group:BUNDLE ${mids.join(" ")}`;
    })
    .filter((line) => line !== null);

  if (removedVideo) {
    const audioIndex = sanitized.findIndex((line) => /^m=audio /.test(line));
    if (audioIndex >= 0) {
      const bundleLine = sanitized.find((line) => /^a=group:BUNDLE /.test(line));
      const bundleMid = bundleLine?.slice("a=group:BUNDLE ".length).split(" ")[0] || "0";
      sanitized = ensureMediaSectionMid(sanitized, audioIndex, bundleMid);
    }
  }

  description.sdp = finalizeSdp(sanitized.join("\r\n"));
  return Promise.resolve(description);
};

/**
 * Keeps only browser-safe audio codecs in remote SDP before setRemoteDescription.
 * Grandstream inbound calls from PSTN trunks often include G726-32 on payload type 2,
 * which triggers: "Duplicate statically assigned payload type".
 */
export const stripNonWebRtcAudioCodecs = (description) => {
  let sdp = description.sdp || "";
  if (!sdp) {
    return Promise.resolve(description);
  }

  const normalized = sdp.includes("\r\n") ? sdp : sdp.replace(/\n/g, "\r\n");
  const lines = normalized.split(/\r\n/);
  const audioSections = [];
  let currentSection = null;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^m=audio /.test(line)) {
      currentSection = { mLineIndex: i, strippedPayloadTypes: [] };
      audioSections.push(currentSection);
      continue;
    }
    if (!currentSection) {
      continue;
    }

    const rtpmapMatch = /^a=rtpmap:(\d+) (.+)$/.exec(line);
    if (rtpmapMatch) {
      const payloadType = rtpmapMatch[1];
      const codecPart = rtpmapMatch[2];
      if (!WEBRTC_AUDIO_CODEC.test(codecPart)) {
        currentSection.strippedPayloadTypes.push(payloadType);
        lines.splice(i, 1);
        i -= 1;
      }
    }
  }

  const strippedSet = new Set(
    audioSections.flatMap((section) => section.strippedPayloadTypes)
  );

  audioSections.forEach((section) => {
    const parts = lines[section.mLineIndex].split(" ");
    const kept = parts.slice(0, 3);
    for (let j = 3; j < parts.length; j += 1) {
      if (!section.strippedPayloadTypes.includes(parts[j])) {
        kept.push(parts[j]);
      }
    }
    lines[section.mLineIndex] = kept.join(" ");
  });

  sdp = lines
    .filter((line) => {
      const fmtpMatch = /^a=fmtp:(\d+) /.exec(line);
      return !(fmtpMatch && strippedSet.has(fmtpMatch[1]));
    })
    .join("\r\n");

  STRIP_LEGACY_AUDIO_PAYLOADS.forEach((payloadName) => {
    sdp = stripPayloadFromSdp(sdp, payloadName);
  });

  description.sdp = finalizeSdp(sdp);
  return Promise.resolve(description);
};

export const WEBRTC_SDP_MODIFIERS = [
  onlyForRemoteDescription(stripVideoSections),
  onlyForRemoteDescription(stripNonWebRtcAudioCodecs),
  // Prefer G.711 via setCodecPreferences in SDH factory — do not rewrite answer SDP.
  onlyForLocalSdp(preferRoutableIceCandidates),
];

export const sessionOptionsWithSdpModifiers = (options = {}) => ({
  ...options,
  sessionDescriptionHandlerModifiers: WEBRTC_SDP_MODIFIERS,
});
